import { useState } from "react";
import { useRoute } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Issue, IssueActivity, IssueAttachment } from "@shared/schema";
import { DetailPageShell } from "@/components/layout/detail-page-shell";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageSquarePlus } from "lucide-react";
import { format } from "date-fns";
import { ActivityTimeline } from "@/components/issues/activity-timeline";
import { AddNoteDialog } from "@/components/issues/add-note-dialog";
import { ReworkConfirmDialog } from "@/components/issues/rework-confirm-dialog";
import { ScreenshotsTabContent } from "@/components/issues/screenshots-tab-content";
import {
  SEVERITY_COLORS,
  PRIORITY_LABELS,
  SEVERITY_LABELS,
  STATUS_LABELS,
  ENVIRONMENT_LABELS,
  CATEGORY_LABELS,
  MODULE_OPTIONS,
} from "@/lib/constants/issue-constants";
import { InlineTextField } from "@/components/inline-edit/inline-text-field";
import { InlineTextareaField } from "@/components/inline-edit/inline-textarea-field";
import { InlineSelectField } from "@/components/inline-edit/inline-select-field";
import { apiRequest, getAuthHeaders } from "@/lib/queryClient";
import { useTabState } from "@/hooks/use-tab-state";

export default function IssueDetail() {
  const [, params] = useRoute("/issues/:id");
  const [activeTab, setActiveTab] = useTabState("overview");
  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  const [reworkDialogOpen, setReworkDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: issue, isLoading } = useQuery<Issue>({
    queryKey: ["/api/crm/issues", params?.id],
    enabled: !!params?.id,
  });

  // Fetch sprint data if exists (by sprint number, not UUID)
  const {
    data: sprint,
    isLoading: sprintLoading,
  } = useQuery({
    queryKey: ["/api/project-manager/sprints/by-number", issue?.sprintId],
    queryFn: async () => {
      if (!issue?.sprintId) return null;
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`/api/project-manager/sprints/by-number/${issue.sprintId}`, {
        headers: authHeaders,
        credentials: "include",
      });
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`Failed to fetch sprint: ${response.statusText}`);
      }
      return response.json();
    },
    enabled: !!issue?.sprintId,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch linked checklist item if exists
  const {
    data: checklistItem,
    isLoading: checklistItemLoading,
  } = useQuery({
    queryKey: ["/api/testing/checklist-items", issue?.checklistItemId],
    queryFn: async () => {
      if (!issue?.checklistItemId) return null;
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`/api/testing/checklist-items/${issue.checklistItemId}`, {
        headers: authHeaders,
        credentials: "include",
      });
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`Failed to fetch checklist item: ${response.statusText}`);
      }
      return response.json();
    },
    enabled: !!issue?.checklistItemId,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch issue activity log (uses default queryFn with auth headers)
  const { data: activities = [] } = useQuery<IssueActivity[]>({
    queryKey: ["/api/crm/issues", params?.id, "activity"],
    enabled: !!params?.id,
  });

  // Fetch issue screenshots (uses default queryFn with auth headers)
  const { data: screenshotsData, isLoading: isLoadingScreenshots } = useQuery<{ screenshots: IssueAttachment[]; totalCount: number; totalSize: string }>({
    queryKey: ["/api/crm/issues", params?.id, "screenshots"],
    enabled: !!params?.id,
  });

  const screenshots = screenshotsData?.screenshots || [];

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Issue>) => {
      return await apiRequest("PATCH", `/api/crm/issues/${params?.id}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/issues"] });
      // Refresh activity log after update
      queryClient.invalidateQueries({ queryKey: ["/api/crm/issues", params?.id, "activity"] });
    },
  });

  const handleFieldUpdate = async (field: keyof Issue, value: any): Promise<void> => {
    // REWORK DETECTION: Intercept status changes from user_final_verify to rework
    if (
      field === "status" &&
      issue?.status === "user_final_verify" &&
      value === "rework"
    ) {
      // Show rework confirmation dialog
      setPendingStatusChange(value);
      setReworkDialogOpen(true);
      return;
    }

    // Normal update
    await updateMutation.mutateAsync({ [field]: value });
  };

  const handleReworkConfirm = async (_reworkNote: string) => {
    setReworkDialogOpen(false);
    // Note: reworkNote is passed separately to a rework endpoint, not part of issue update
    await updateMutation.mutateAsync({
      status: pendingStatusChange as "review" | "open" | "investigating" | "fix_in_progress" | "user_final_verify" | "closed" | "wont_fix" | "deferred" | "duplicate" | "rework" | undefined,
    });
    setPendingStatusChange(null);
  };

  const handleReworkCancel = async () => {
    setReworkDialogOpen(false);
    // User chose "Just Change Status" - do normal status change
    if (pendingStatusChange) {
      await updateMutation.mutateAsync({ status: pendingStatusChange as "review" | "open" | "investigating" | "fix_in_progress" | "user_final_verify" | "closed" | "wont_fix" | "deferred" | "duplicate" | "rework" });
    }
    setPendingStatusChange(null);
  };

  // Options for inline select fields
  const statusOptions = Object.entries(STATUS_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  const severityOptions = Object.entries(SEVERITY_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  const priorityOptions = Object.entries(PRIORITY_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  const moduleOptions = MODULE_OPTIONS.map((module) => ({
    value: module,
    label: module,
  }));

  if (isLoading) {
    return <div className="p-8">Loading issue...</div>;
  }

  if (!issue) {
    return <div className="p-8">Issue not found</div>;
  }

  return (
    <>
      <DetailPageShell
        title={issue.title}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Issues", href: "/issues" },
          { label: issue.issueNumber },
        ]}
        badge={
          <Badge variant="outline" className={SEVERITY_COLORS[issue.severity]}>
            {SEVERITY_LABELS[issue.severity]}
          </Badge>
        }
        navItems={[
          { label: "Overview", href: "#overview" },
          { label: "Screenshots", href: "#screenshots" },
          { label: "Logs", href: "#logs" },
        ]}
        value={activeTab}
        onValueChange={setActiveTab}
        onNavItemClick={(href) => {
          // Extract tab value from href (e.g., "#overview" -> "overview")
          const tabValue = href.replace("#", "");
          setActiveTab(tabValue);
        }}
        tabs={[
          {
            value: "overview",
            label: "Overview",
            content: (
              <div className="space-y-2">
                {/* Issue Details Card */}
                <Card>
                  <CardContent className="pt-3">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                      Issue Details
                    </h3>
                    <div className="space-y-2">
                      <InlineTextField
                        value={issue.title}
                        onSave={(value) => handleFieldUpdate("title", value)}
                        label="Title"
                      />
                      <InlineTextareaField
                        value={issue.description || ""}
                        onSave={(value) => handleFieldUpdate("description", value)}
                        label="Description"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Root Cause & Solution Card */}
                <Card>
                  <CardContent className="pt-3">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                      Root Cause & Solution
                    </h3>
                    <div className="space-y-2">
                      <InlineTextareaField
                        value={issue.rootCause || ""}
                        onSave={(value) => handleFieldUpdate("rootCause", value)}
                        label="Root Cause"
                      />
                      <InlineTextareaField
                        value={issue.solution || ""}
                        onSave={(value) => handleFieldUpdate("solution", value)}
                        label="Solution"
                      />
                      <InlineTextareaField
                        value={issue.prevention || ""}
                        onSave={(value) => handleFieldUpdate("prevention", value)}
                        label="Prevention"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Linked Items Card - Only show Test ID and Test File Path */}
                {(issue.testId || issue.testFilePath) && (
                  <Card>
                    <CardContent className="pt-3">
                      <h3 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                        Linked Items
                      </h3>

                      <div className="space-y-2 text-sm">
                        {/* Test ID */}
                        {issue.testId && (
                          <div>
                            <p className="text-muted-foreground text-xs">Test ID</p>
                            <p className="font-medium font-mono text-xs">{issue.testId}</p>
                          </div>
                        )}

                        {/* Test File Path */}
                        {issue.testFilePath && (
                          <div>
                            <p className="text-muted-foreground text-xs">Test File</p>
                            <a
                              href={`https://github.com/edgeview-finance-business/edgeview-finance-crm/blob/main/${issue.testFilePath}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline text-xs font-mono"
                            >
                              {issue.testFilePath}
                            </a>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ),
          },
          {
            value: "screenshots",
            label: "Screenshots",
            content: (
              <div className="space-y-2">
                <ScreenshotsTabContent
                  issueId={params?.id || ""}
                  screenshots={screenshots}
                  isLoading={isLoadingScreenshots}
                />
              </div>
            ),
          },
          {
            value: "logs",
            label: "Logs",
            content: (
              <div className="space-y-2">
                <Card>
                  <CardContent className="pt-3">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                      Activity Timeline
                    </h3>
                    <ActivityTimeline activities={activities} issueId={params?.id || ""} />
                  </CardContent>
                </Card>

                {issue.errorMessage && (
                  <Card>
                    <CardContent className="pt-3">
                      <h3 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                        Error Message
                      </h3>
                      <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto whitespace-pre-wrap">
                        <code>{issue.errorMessage}</code>
                      </pre>
                    </CardContent>
                  </Card>
                )}
              </div>
            ),
          },
        ]}
        rightPanel={
          <div className="space-y-4">
            {/* Key Metadata */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                Key Metadata
              </h3>
              <div className="space-y-2">
                <InlineSelectField
                  value={issue.status}
                  options={statusOptions}
                  onSave={(value) => handleFieldUpdate("status", value)}
                  label="Status"
                  testId="issue-status"
                />
                <InlineSelectField
                  value={issue.severity}
                  options={severityOptions}
                  onSave={(value) => handleFieldUpdate("severity", value)}
                  label="Severity"
                  testId="issue-severity"
                />
                <InlineSelectField
                  value={issue.priority}
                  options={priorityOptions}
                  onSave={(value) => handleFieldUpdate("priority", value)}
                  label="Priority"
                  testId="issue-priority"
                />
                <InlineSelectField
                  value={issue.module}
                  options={moduleOptions}
                  onSave={(value) => handleFieldUpdate("module", value)}
                  label="Module"
                  testId="issue-module"
                />
                <div>
                  <p className="text-muted-foreground text-sm font-medium mb-1">Environment</p>
                  <Badge variant="outline">{ENVIRONMENT_LABELS[issue.environment]}</Badge>
                </div>
                {issue.category && (
                  <div>
                    <p className="text-muted-foreground text-sm font-medium mb-1">Category</p>
                    <p className="font-medium text-xs">{CATEGORY_LABELS[issue.category]}</p>
                  </div>
                )}

                {/* Sprint Number */}
                {issue.sprintId && (
                  <div>
                    <p className="text-muted-foreground text-sm font-medium mb-1">Sprint</p>
                    {sprintLoading ? (
                      <p className="text-xs text-muted-foreground">Loading...</p>
                    ) : sprint ? (
                      <a
                        href={`/project-manager/sprints/${sprint.id}`}
                        className="text-primary hover:underline text-xs font-medium"
                      >
                        {sprint.sprintNumber} - {sprint.sprintName || "Untitled Sprint"}
                      </a>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {issue.sprintId} (not found)
                      </p>
                    )}
                  </div>
                )}

                {/* Test Checklist */}
                {issue.checklistItemId && (
                  <div>
                    <p className="text-muted-foreground text-sm font-medium mb-1">Test Checklist</p>
                    {checklistItemLoading ? (
                      <p className="text-xs text-muted-foreground">Loading...</p>
                    ) : checklistItem ? (
                      <a
                        href={`/project-manager/testing-checklists/${checklistItem.checklistId}?item=${issue.checklistItemId}`}
                        className="text-primary hover:underline text-xs"
                      >
                        {checklistItem.testId}
                      </a>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Item not found
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <p className="text-muted-foreground text-sm font-medium mb-1">Created</p>
                  <p className="font-medium text-xs">{format(new Date(issue.createdAt), "MMM d, yyyy")}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-sm font-medium mb-1">Updated</p>
                  <p className="font-medium text-xs">{format(new Date(issue.updatedAt), "MMM d, yyyy")}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="border-t pt-3">
              <h3 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                Actions
              </h3>
              <div className="space-y-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full justify-start text-xs"
                  onClick={() => setAddNoteDialogOpen(true)}
                >
                  <MessageSquarePlus className="w-3 h-3 mr-2" />
                  Add Note
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full justify-start text-xs"
                  onClick={() => handleFieldUpdate("status", "closed")}
                  disabled={issue.status === "closed"}
                >
                  Close Issue
                </Button>
              </div>
            </div>
          </div>
        }
      />

      {/* Dialogs rendered outside DetailPageShell to avoid layout constraints */}
      {params?.id && (
        <>
          <AddNoteDialog
            open={addNoteDialogOpen}
            onOpenChange={setAddNoteDialogOpen}
            issueId={params.id}
          />
          <ReworkConfirmDialog
            open={reworkDialogOpen}
            onOpenChange={setReworkDialogOpen}
            onConfirm={handleReworkConfirm}
            onCancel={handleReworkCancel}
          />
        </>
      )}
    </>
  );
}
